import { useDisclosure } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  CONTENT_INNER_PX_SIZE,
  HEADER_ID,
  SIDEBAR_ID,
} from '@/components/templates/PageLayout';
import {
  CmBaseTableWithQuestionIcon,
  CmBox,
  CmCard,
  CmFlex,
  CmHeadingPagination,
  CmLongLoading,
  CmMessageCard,
  CmScrollBox,
  CmTableBodyRow,
  CmTd,
  CmText,
  CmTooltip,
  useCounter,
} from '@/components/uiParts';
import { LIMIT_PER_PAGE } from '@/domain/models/Pagination';
import { useTargetsDomHeight, useTargetsDomWidth } from '@/hooks/Dom';
import { redirect404Page } from '@/lib/AccessControl';
import { ommisionText } from '@/lib/util';
import { BrandSidebarForm } from '@/pages/store-management/brands/components/BrandSidebarForm';
import { brandTableThs } from '@/pages/store-management/brands/service';
import { StoreManegementPageLayout } from '@/pages/store-management/components/Layout/StoreManegementPageLayout';
import { routes } from '@/router';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';
import {
  StoreManagementBrandSearchList,
  useStoreManagementBrandSearchList,
} from '@/services/StoreManagement/Brands/hooks';
export const SEARCH_AREA_ID = 'search-form';

const StoreManagementBrandsPage: React.FC = (): JSX.Element | null => {
  const history = useHistory();
  const { managerAccountMe } = useManagerAccountMe();
  // /* アクセス制御 */
  redirect404Page('store_management', history, managerAccountMe);
  const [brands, setBrands] = useState<StoreManagementBrandSearchList[]>([]);
  const [searchCount, setSearchCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { count, decrement, increment, customizeCount } = useCounter(1);
  const { targetsHeight } = useTargetsDomHeight([HEADER_ID, SEARCH_AREA_ID]);
  const getListQuery = {
    currentPage: 1,
    limit: LIMIT_PER_PAGE,
  };
  const [targetBrand, setTargetBrand] =
    useState<StoreManagementBrandSearchList>();

  const {
    isOpen: isSidebarOpen,
    onOpen: handleOpenSidebar,
    onClose: handleCloseSidebar,
  } = useDisclosure();

  /* ページネーション */
  const toFirstPage = async () => {
    setIsLoading(true);
    const { brands } = await useStoreManagementBrandSearchList({
      ...getListQuery,
    });
    setBrands(brands), customizeCount(1), setIsLoading(false);
  };

  const toPreviousPage = async () => {
    setIsLoading(true);
    const previousPage = count - 1;
    const { brands } = await useStoreManagementBrandSearchList({
      ...getListQuery,
      currentPage: previousPage,
    });
    setBrands(brands), decrement(), setIsLoading(false);
  };

  const toNextPage = async () => {
    setIsLoading(true);
    const nextPage = count + 1;
    const { brands } = await useStoreManagementBrandSearchList({
      ...getListQuery,
      currentPage: nextPage,
    });
    setBrands(brands), increment(), setIsLoading(false);
  };

  const toLastPage = async () => {
    setIsLoading(true);
    const lastPage = Math.ceil(searchCount / LIMIT_PER_PAGE);
    const { brands } = await useStoreManagementBrandSearchList({
      ...getListQuery,
      currentPage: lastPage,
    });
    setBrands(brands), customizeCount(lastPage), setIsLoading(false);
  };
  const handleOpenSideBar = async (brand: StoreManagementBrandSearchList) => {
    setTargetBrand(brand);
    handleOpenSidebar();
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const { brands, count } = await useStoreManagementBrandSearchList({
        ...getListQuery,
      });
      customizeCount(1);
      setBrands(brands);
      setSearchCount(count);
      setIsLoading(false);
    })();
  }, []);
  const handleInitializeBrands = async () => {
    handleCloseSidebar();
    const { brands, count } = await useStoreManagementBrandSearchList(
      getListQuery
    );
    setBrands(brands);
    setSearchCount(count);
  };

  const formComponsent = (
    <BrandSidebarForm
      {...{
        isSidebarOpen,
        targetBrand,
        handleCloseSidebar,
        handleInitializeBrands,
      }}
    />
  );

  const { targetsWidth: sidebarWidth } = useTargetsDomWidth([SIDEBAR_ID]);
  return (
    <StoreManegementPageLayout
      title={routes.storeManagementBrands.title}
      {...{
        formComponsent,
      }}
    >
      <CmBox>
        <CmBox padding="20px 20px 20px 0" id={SEARCH_AREA_ID}>
          <CmFlex alignItems="flex-end" justifyContent="space-between" w="full">
            <CmBox />
            <CmBox width="40%" textAlign="right">
              件数: {searchCount} 件
            </CmBox>
          </CmFlex>
        </CmBox>

        {brands.length > 0 ? (
          <CmCard
            padding="20px"
            height={`calc(100vh - ${targetsHeight}px - 20px)`}
            w={`calc(100vw - ${sidebarWidth + CONTENT_INNER_PX_SIZE * 2}px)`}
          >
            <CmFlex flexDirection="row-reverse" my="5px">
              <CmHeadingPagination
                totalNum={searchCount}
                currentPage={count}
                LimitPerPage={LIMIT_PER_PAGE}
                {...{ toFirstPage, toNextPage, toPreviousPage, toLastPage }}
              />
            </CmFlex>
            <CmScrollBox>
              <CmBaseTableWithQuestionIcon ths={brandTableThs}>
                {brands.map((brand, index) => {
                  const { name } = brand;
                  return (
                    <CmTableBodyRow key={index + 1}>
                      <CmTd>
                        <CmBox
                          tabIndex={0}
                          cursor="pointer"
                          padding="5px"
                          onClick={async () => await handleOpenSideBar(brand)}
                        >
                          <CmText>
                            <CmTooltip label={name}>
                              {ommisionText(name, 20)}
                            </CmTooltip>
                          </CmText>
                        </CmBox>
                      </CmTd>
                    </CmTableBodyRow>
                  );
                })}
              </CmBaseTableWithQuestionIcon>
            </CmScrollBox>
          </CmCard>
        ) : (
          <>
            {!isLoading && (
              <CmMessageCard margin="100px auto 0">
                ブランドが登録されていません。
              </CmMessageCard>
            )}
          </>
        )}
        {isLoading && <CmLongLoading />}
      </CmBox>
    </StoreManegementPageLayout>
  );
};

export default StoreManagementBrandsPage;
